import mapboxgl, { FitBoundsOptions, LngLatBoundsLike, LngLatLike } from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import * as React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { MAPBOX_ACCESS_TOKEN } from '../../../util/constants'
import Loading from '../misc/Loading'
import MapContext from './MapContext'
import { addMissingMarker } from './markers'

// Configure mapbox-gl CSP worker via blob URL for cross-origin CDN compatibility.
// Vite's ?url import gives a CDN path (e.g., cloudfront.net), but Workers must be
// same-origin. Wrapping in a blob URL makes the browser treat it as same-origin.
import mapboxglWorkerUrl from 'mapbox-gl/dist/mapbox-gl-csp-worker?url'
const workerBlob = new Blob([`importScripts("${mapboxglWorkerUrl}");`], { type: 'text/javascript' })
// eslint-disable-next-line @typescript-eslint/no-explicit-any
;(mapboxgl as any).workerUrl = URL.createObjectURL(workerBlob)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
;(mapboxgl as any).accessToken = MAPBOX_ACCESS_TOKEN

const DEFAULT_STYLE = 'mapbox://styles/mapbox/light-v10'

// mapbox-gl throws "Invalid LngLat object: (NaN, NaN)" for anything that is not a finite lng/lat.
export const isValidLngLat = (center?: LngLatLike | null): boolean => {
  if (!center) return false
  const [lng, lat] = Array.isArray(center)
    ? center
    : 'lon' in center
      ? [center.lon, center.lat]
      : [(center as { lng: number }).lng, (center as { lat: number }).lat]
  return Number.isFinite(lng) && Number.isFinite(lat)
}

interface Props {
  center?: LngLatLike
  zoom?: number[]
  fitBounds?: LngLatBoundsLike | null
  fitBoundsOptions?: FitBoundsOptions
  onClick?: (map: mapboxgl.Map, event: mapboxgl.MapMouseEvent) => void
  onStyleLoad?: (map: mapboxgl.Map) => void
  onSizeUpdate?: (rect: DOMRectReadOnly) => void
  /**
   * Class for the map's container. With a className the map no longer fills its parent's
   * height, so the class's stylesheet must give it a height (as .place-map does), or the map
   * draws blank. Without one the map is 100% of its parent's height, which must have one.
   */
  className?: string
  loading?: boolean
  style?: string
  mapOptions?: Partial<mapboxgl.MapboxOptions>
  flyToOptions?: mapboxgl.FlyToOptions
  animationOptions?: mapboxgl.AnimationOptions
  children?: React.ReactNode
}

const Mapbox: React.FC<Props> = ({
  children,
  center,
  zoom,
  fitBounds,
  fitBoundsOptions,
  onClick,
  onStyleLoad,
  onSizeUpdate,
  className,
  loading: parentIsLoading,
  style = DEFAULT_STYLE,
  mapOptions,
  flyToOptions,
  animationOptions,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const [map, setMap] = useState<mapboxgl.Map | null>(null)
  const [mapIsLoading, setMapIsLoading] = useState(true)

  const loading = Boolean(mapIsLoading || parentIsLoading)

  // Stable refs for callbacks to avoid stale closures
  const onStyleLoadRef = useRef(onStyleLoad)
  onStyleLoadRef.current = onStyleLoad
  const onClickRef = useRef(onClick)
  onClickRef.current = onClick

  // Initialize map
  useEffect(() => {
    if (!containerRef.current) return

    const m = new mapboxgl.Map({
      container: containerRef.current,
      style,
      center: isValidLngLat(center) ? center : [0, 0],
      zoom: zoom?.[0] ?? 9,
      ...mapOptions,
    })

    m.on('load', () => {
      setMapIsLoading(false)
      onStyleLoadRef.current?.(m)
    })

    m.on('styleimagemissing', e => addMissingMarker({ map: m, name: e.id }))

    mapRef.current = m
    setMap(m)

    // Fallback loading timeout (in case load event is slow)
    const timer = setTimeout(() => setMapIsLoading(false), 3000)

    return () => {
      clearTimeout(timer)
      m.remove()
      mapRef.current = null
      setMap(null)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Click handler
  useEffect(() => {
    if (!map) return

    const handler = (e: mapboxgl.MapMouseEvent) => {
      onClickRef.current?.(map, e)
    }
    map.on('click', handler)

    return () => {
      map.off('click', handler)
    }
  }, [map])

  // fitBounds reactivity
  useEffect(() => {
    if (map && fitBounds) {
      map.fitBounds(fitBounds, fitBoundsOptions)
    }
  }, [map, fitBounds, fitBoundsOptions])

  // Center/zoom reactivity (only when fitBounds is not controlling position)
  const initialRender = useRef(true)
  useEffect(() => {
    if (!map || fitBounds) return
    // Skip the initial render since the map was already created with these values
    if (initialRender.current) {
      initialRender.current = false
      return
    }
    if (!isValidLngLat(center)) return

    map.flyTo({
      center: center as [number, number],
      zoom: zoom?.[0],
      ...flyToOptions,
      ...animationOptions,
    })
  }, [map, center, zoom]) // eslint-disable-line react-hooks/exhaustive-deps

  // ResizeObserver (replaces SizeUpdater)
  const onSizeUpdateRef = useRef(onSizeUpdate)
  onSizeUpdateRef.current = onSizeUpdate

  const handleResize = useCallback((entries: ResizeObserverEntry[]) => {
    mapRef.current?.resize()
    if (entries[0]) {
      onSizeUpdateRef.current?.(entries[0].contentRect)
    }
  }, [])

  useEffect(() => {
    if (!containerRef.current) return

    const ro = new ResizeObserver(handleResize)
    ro.observe(containerRef.current)

    return () => ro.disconnect()
  }, [handleResize])

  return (
    <MapContext.Provider value={map}>
      <div
        ref={containerRef}
        className={className}
        // A className's stylesheet sets the height (e.g. .place-map); 100% of a parent with no
        // height (the place page's link) would collapse the map to nothing.
        style={className ? { width: '100%', position: 'relative' } : { width: '100%', height: '100%', position: 'relative' }}
      />
      {children}
      {loading && (
        <div className="splash-screen">
          <Loading className="isDark" />
        </div>
      )}
    </MapContext.Provider>
  )
}

export default Mapbox
